import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { Download } from '@mui/icons-material';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../../providers/GlobalProvider';
import {
  useBulkUploadEntities,
  useCreateBulkEntitiesWithoutDuplicateCheck,
} from '../../../api/bitedb/entities/entities.hooks';
import { DuplicateReviewContent } from '../Components/DuplicateReviewContent';
import {
  useBulkUploadTariffs,
  useCreateBulkTariffsWithoutDuplicateCheck,
} from 'api/bitedb/tariffs/tariffs.hooks';
import { useBulkUploadEvents } from 'hooks/useSocketIO';
import { DuplicateEntity } from 'api/bitedb/entities/entities';
import {
  productTemplateColumns,
  useBulkUploadProducts,
  useCreateBulkProductsWithoutDuplicateCheck,
} from '../../../api/bitedb/products/products.hooks';
import { useCreateBulkEntityRelationships } from '../../../api/bitedb/entityRelationships/entityRelationships.hooks';
import type { ParsedRelationshipRow } from '../../../utils/biteDbRelationshipTemplates';
import EntityRelationshipImportStep from '../Components/EntityRelationshipImportStep';
import CreditsAPI from '../../../api/CreditsAPI';
import InsufficientCredits from '../../../pages/credit-plans/InsufficientCredits';
import { csvJSON } from '../../../utils/CsvToJson';
import * as XLSX from 'xlsx';

interface IBulkUploadModalProps {
  open: boolean;
  handleClose: () => void;
  title?: string;
  database: any;
  handleRefresh?: any;
  onDatabaseUploadFailed?: (databaseId: string) => void;
  onDatabaseUploadStarted?: (databaseId: string) => void;
  onDatabaseUploadCompleted?: (databaseId: string) => void;
  onRequestScreeningCheck?: () => void;
}

export const BulkUploadModal: React.FC<IBulkUploadModalProps> = ({
  open,
  handleClose,
  title = 'Bulk Upload Items',
  database,
  handleRefresh,
  onDatabaseUploadFailed,
  onDatabaseUploadStarted,
  onDatabaseUploadCompleted,
  onRequestScreeningCheck,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [hasDuplicates, setHasDuplicates] = useState<boolean>(false);
  const [duplicatesFound, setDuplicatesFound] = useState<DuplicateEntity[]>([]);
  const [modalStep, setModalStep] = useState<
    'upload' | 'duplicates' | 'relationships'
  >('upload');
  const { cur_user } = useContext(GlobalContext);

  // Relationship import state
  const [parsedRelationships, setParsedRelationships] = useState<
    ParsedRelationshipRow[]
  >([]);
  const [isUploadingRelationships, setIsUploadingRelationships] =
    useState(false);

  // Entity Hooks
  const bulkUploadMutation = useBulkUploadEntities();
  const createBulkWODupeCheck = useCreateBulkEntitiesWithoutDuplicateCheck();

  // Tariff Hooks
  const bulkUploadTariffMutation = useBulkUploadTariffs();
  const createBulkTariffsWODupeCheck =
    useCreateBulkTariffsWithoutDuplicateCheck();

  // Product Hooks
  const bulkUploadProductMutation = useBulkUploadProducts();
  const createBulkProductsWODupeCheck =
    useCreateBulkProductsWithoutDuplicateCheck();

  // Relationship Hooks
  const bulkRelationshipMutation = useCreateBulkEntityRelationships();

  const [activeJobId, setActiveJobId] = useState<string | undefined>();
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [requiredDatabaseCredits, setRequiredDatabaseCredits] = useState(0);
  const isUploading = useRef(false);
  const uploadStartTimeRef = useRef<string | null>(null);

  const getUploadRowCount = useCallback(
    async (currentFile: File): Promise<number> => {
      if (
        currentFile.name.endsWith('.csv') ||
        currentFile.type === 'text/csv'
      ) {
        const text = await currentFile.text();
        const rows = csvJSON(text);
        return Array.isArray(rows) ? rows.length : 0;
      }
      if (currentFile.name.endsWith('.xlsx')) {
        const buffer = await currentFile.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
        }) as any[][];
        return Math.max(0, data.length - 1);
      }
      return 0;
    },
    [],
  );

  // Determine if we are working with tariffs
  const isTariffDB = database?.type === 'tariffs';
  const isProductDB = database?.type === 'products';
  const isEntityDB = !isTariffDB && !isProductDB;

  const goToRelationshipsStep = useCallback(() => {
    setModalStep('relationships');
  }, []);

  const normalizeDuplicates = useCallback(
    (rawDuplicates?: any[]): DuplicateEntity[] => {
      if (!Array.isArray(rawDuplicates) || rawDuplicates.length === 0) {
        return [];
      }

      if (!isTariffDB) {
        return rawDuplicates as DuplicateEntity[];
      }

      return rawDuplicates.map((duplicate: any) => ({
        ...duplicate,
        entity: duplicate.tariff || duplicate.entity,
      }));
    },
    [isTariffDB],
  );

  const shouldHandleEvent = useCallback(
    (event: { databaseId?: string; jobId?: string }) => {
      const jobMatches =
        !activeJobId || !event.jobId || event.jobId === activeJobId;
      const dbId =
        database?.id !== undefined && database?.id !== null
          ? database.id.toString()
          : undefined;
      const dbMatches = !dbId || !event.databaseId || event.databaseId === dbId;
      return jobMatches && dbMatches;
    },
    [activeJobId, database?.id],
  );

  // Listen for bulk upload events for this specific job
  useBulkUploadEvents(activeJobId, {
    onComplete: (result) => {
      if (!shouldHandleEvent(result)) {
        return;
      }

      setActiveJobId(undefined);

      if (database?.id) {
        onDatabaseUploadCompleted?.(database.id.toString());
      }

      const normalizedDuplicates = normalizeDuplicates(result.duplicates);
      if (normalizedDuplicates.length > 0) {
        setHasDuplicates(true);
        setDuplicatesFound(normalizedDuplicates);
        setModalStep('duplicates');
        enqueueSnackbar(
          `${normalizedDuplicates.length} potential duplicate${
            normalizedDuplicates.length > 1 ? 's' : ''
          } require review before screening continues.`,
          { variant: 'warning' },
        );
        return;
      }

      if (isEntityDB) {
        goToRelationshipsStep();
        return;
      }
      handleRefresh?.();
      handleModalClose();
    },
    onFailed: (result) => {
      if (!shouldHandleEvent(result)) {
        return;
      }
      console.error('[BulkUploadModal] Upload failed:', result);
      enqueueSnackbar(result.error || 'Upload failed', { variant: 'error' });
      if (database?.id) {
        onDatabaseUploadFailed?.(database.id.toString());
      }
      setActiveJobId(undefined);
    },
  });

  const resetModalState = useCallback(() => {
    setFile(null);
    setHasDuplicates(false);
    setDuplicatesFound([]);
    setModalStep('upload');
    setActiveJobId(undefined);
    uploadStartTimeRef.current = null;
    setParsedRelationships([]);
    setIsUploadingRelationships(false);
  }, []);

  const handleModalClose = useCallback(() => {
    resetModalState();
    handleClose();
  }, [handleClose, resetModalState]);

  const handleRelationshipsUpload = useCallback(async () => {
    if (parsedRelationships.length === 0) {
      handleRefresh?.();
      handleModalClose();
      onRequestScreeningCheck?.();
      return;
    }

    setIsUploadingRelationships(true);
    try {
      const result = await bulkRelationshipMutation.mutateAsync({
        payload: {
          relationships: parsedRelationships,
          database_id: database?.id,
        },
        teamId: cur_user.selected_team?.id,
      });

      if (result?.success) {
        const inserted = result.data?.inserted?.length || 0;
        const failed = result.data?.failed?.length || 0;
        const unresolved = result.data?.unresolved?.length || 0;
        let msg = `${inserted} relationship${
          inserted !== 1 ? 's' : ''
        } created`;
        if (failed > 0) msg += `, ${failed} failed`;
        if (unresolved > 0) msg += `, ${unresolved} unresolved`;
        enqueueSnackbar(msg, { variant: inserted > 0 ? 'success' : 'warning' });
      } else {
        enqueueSnackbar(result?.message || 'Failed to create relationships', {
          variant: 'error',
        });
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to upload relationships', {
        variant: 'error',
      });
    } finally {
      setIsUploadingRelationships(false);
      handleRefresh?.();
      handleModalClose();
      onRequestScreeningCheck?.();
    }
  }, [
    parsedRelationships,
    bulkRelationshipMutation,
    database?.id,
    cur_user.selected_team?.id,
    handleRefresh,
    handleModalClose,
    onRequestScreeningCheck,
  ]);

  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (
      bulkUploadMutation.isPending ||
      bulkUploadProductMutation.isPending ||
      bulkUploadTariffMutation.isPending
    ) {
      return;
    }
    const accptFile = acceptedFiles[0];
    if (!accptFile) {
      return;
    }

    const fileNew = new File([accptFile], accptFile.name, {
      type: accptFile.type,
    });
    setFile(fileNew);

    try {
      await fileHandler(fileNew);
      enqueueSnackbar('File ready for submission!', { variant: 'success' });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error processing file. Please ensure it is a valid CSV or XLSX.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setFile(null);
    }
  };

  const fileHandler = async (file: any) => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (
          file.type === 'text/csv' ||
          file.name.endsWith('.csv') ||
          file.name.endsWith('.xlsx')
        ) {
          resolve({
            name: file.name,
            size: file.size,
            type: file.type,
          });
        } else {
          console.error(
            'Mock fileHandler error: Unsupported file type:',
            file.type,
            file.name,
          );
          reject(
            new Error(
              'Mock fileHandler: Unsupported file type. Please upload CSV or XLSX.',
            ),
          );
        }
      }, 500);
    });
  };

  const exportToCSV = (filename: string, data: any, headers: any) => {
    const csvRows = [];
    csvRows.push(headers.join(','));
    data.forEach((item: any) => {
      const values = headers.map((header: any) => {
        const escaped = `${item[header]}`.replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    });
    const csvString = csvRows.join('\r\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      enqueueSnackbar('Browser does not support link.download attribute.', {
        variant: 'error',
      });
    }
  };

  const fileValidator = (currentFile: File) => {
    if (currentFile.size > 10 * 1024 * 1024) {
      enqueueSnackbar('Your file is too large, upload a file less than 10MB', {
        variant: 'error',
      });
      return { code: 'size-too-large', message: 'File is larger than 10MB' };
    }
    const acceptedMimeTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ];
    const acceptedExtensions = ['.csv', '.xlsx'];
    const fileExtension = `.${currentFile.name
      .split('.')
      .pop()
      ?.toLowerCase()}`;

    if (
      !acceptedMimeTypes.includes(currentFile.type) &&
      !acceptedExtensions.includes(fileExtension)
    ) {
      enqueueSnackbar(
        'Invalid file type. Please upload a CSV or Excel (XLSX) file.',
        { variant: 'error' },
      );
      return {
        code: 'file-type-invalid',
        message: 'File type must be one of: .csv, .xlsx',
      };
    }
    return null;
  };

  const handleDeleteFile = () => {
    setFile(null);
    enqueueSnackbar('File removed.', { variant: 'info' });
  };

  const templateHeaders = useMemo(
    () => [
      'name',
      'alias',
      'description',
      'email',
      'phone',
      'address',
      'address1',
      'address2',
      'country_of_origin',
      'risk_level',
      'category',
      'subCategory',
      'business_unit',
      'parent_business',
      'sku_product_id',
      'supplier_vendor_id',
      'business_registration_number',
      'person_id',
      'manufacturer_identification_number',
      'domain',
    ],
    [],
  );

  const templateTariffHeaders = useMemo(
    () => [
      'HTS Code (10 dgit)',
      'Country of Origin (ISO-2)',
      'Product Value',
      'Transportation Method',
      'Entry Date (YYYY-MM-DD)',
      'Loading Date (YYYY-MM-DD)',
      'Country of Smelt / Pour (ISO-2)',
      'Material Percentage (.xx)',
      // Reported quantity + its unit. Needed to price the specific (per-unit)
      // portion of a duty rate such as "2.3% + $1.05/kg"; without them that
      // component contributes $0 and the line is flagged "Specific Rate
      // Missing Unit Quantity". Free-text UOM ("KG", "NO", "DOZ", "LITER").
      'Quantity',
      'Unit of Measure',
      'Ad Valorem',
      'Use FTA (Y/N)',
      'Product Code',
    ],
    [],
  );

  const templateProductHeaders = useMemo(() => productTemplateColumns, []);

  const handleDownloadTemplate = () => {
    if (isTariffDB) {
      exportToCSV('tariff_template.csv', [], templateTariffHeaders);
    } else if (isProductDB) {
      exportToCSV('product_template.csv', [], templateProductHeaders);
    } else {
      exportToCSV('supplier_template.csv', [], templateHeaders);
    }
    enqueueSnackbar('Template download initiated.', { variant: 'info' });
  };

  // ✅ Main Logic to switch between Tariff and Entity uploads
  const handleBulkUpload = async () => {
    if (isUploading.current) {
      return;
    }
    if (!file) {
      enqueueSnackbar('Please upload a file first', { variant: 'error' });
      return;
    }
    if (
      bulkUploadMutation.isPending ||
      bulkUploadProductMutation.isPending ||
      bulkUploadTariffMutation.isPending
    ) {
      enqueueSnackbar('File is currently being processed. Please wait.', {
        variant: 'warning',
      });
      return;
    }

    try {
      if (cur_user?.selected_team?.id) {
        const [rowCount, creditsSummaryResponse] = await Promise.all([
          getUploadRowCount(file),
          CreditsAPI.getCreditsSummary(cur_user.selected_team.id),
        ]);
        setRequiredDatabaseCredits(rowCount);

        if (!creditsSummaryResponse?.success) {
          throw new Error('Unable to validate database size allowance.');
        }

        const dbSize = Number(creditsSummaryResponse?.result?.db_size ?? 0);
        if (rowCount > dbSize) {
          setShowInsufficientCredits(true);
          return;
        }
      }

      const normalizeToCSV = async (sourceFile: File): Promise<File> => {
        if (!sourceFile.name.endsWith('.xlsx')) {
          return sourceFile;
        }
        const buffer = await sourceFile.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const csvString = XLSX.utils.sheet_to_csv(
          workbook.Sheets[workbook.SheetNames[0]],
        );
        const csvFileName = sourceFile.name.replace(/\.xlsx$/i, '.csv');
        return new File([csvString], csvFileName, { type: 'text/csv' });
      };

      isUploading.current = true;
      uploadStartTimeRef.current = new Date().toISOString();
      await fileHandler(file);

      const uploadFile = await normalizeToCSV(file);

      const commonPayload = {
        databaseId: database.id,
        file: uploadFile,
        createdBy: cur_user.basics?.id || '',
        teamId: cur_user.selected_team?.id || '',
      };

      const mutationToUse = (
        isTariffDB
          ? bulkUploadTariffMutation
          : isProductDB
          ? bulkUploadProductMutation
          : bulkUploadMutation
      ) as any;

      if (database?.id) {
        onDatabaseUploadStarted?.(database.id.toString());
      }

      await new Promise((resolve, reject) => {
        mutationToUse.mutate(commonPayload, {
          onSuccess: (response: any) => {
            // Store JobId if returned (for background processes)
            if (response.jobId) {
              setActiveJobId(response.jobId);
            }

            const normalizedDuplicates = normalizeDuplicates(
              response?.duplicates,
            );

            if (normalizedDuplicates.length > 0) {
              if (database?.id) {
                onDatabaseUploadCompleted?.(database.id.toString());
              }
              setHasDuplicates(true);
              setDuplicatesFound(normalizedDuplicates);
              setModalStep('duplicates');
              enqueueSnackbar(
                `${normalizedDuplicates.length} potential duplicate${
                  normalizedDuplicates.length > 1 ? 's' : ''
                } detected. Choose how to handle them.`,
                { variant: 'warning' },
              );

              // Refresh list if partial success
              if (
                Array.isArray(response.data) &&
                response.data.length &&
                handleRefresh
              ) {
                handleRefresh();
              }
              resolve(null);
              return;
            }

            enqueueSnackbar(
              `${
                isTariffDB ? 'Tariffs' : isProductDB ? 'Products' : 'Entities'
              } added successfully`,
              { variant: 'success' },
            );

            if (isEntityDB) {
              goToRelationshipsStep();
              resolve(null);
              return;
            }

            handleModalClose();
            if (handleRefresh) {
              handleRefresh();
            }
            onRequestScreeningCheck?.();
            resolve(null);
          },
          onError: (error: any) => {
            console.error('Error during bulk upload:', error);
            const errorMessage =
              error?.response?.data?.message ||
              error?.message ||
              'Unexpected error during bulk upload';
            enqueueSnackbar(`Bulk upload failed. ${errorMessage}`, {
              variant: 'error',
            });
            if (database?.id) {
              onDatabaseUploadFailed?.(database.id.toString());
            }
            reject(error);
          },
        });
      });
    } catch (error) {
      console.error('Error processing file:', error);
      enqueueSnackbar(
        'Error processing file. Please ensure it is a valid CSV or XLSX.',
        { variant: 'error' },
      );
    } finally {
      isUploading.current = false;
    }
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop: onDrop,
    validator: fileValidator,
    accept: ['.csv', '.xlsx'],
    multiple: false,
  });

  const dropzoneClassName = useMemo(() => {
    const baseClass = 'dropzone-base';
    if (isDragAccept) {
      return `${baseClass} dropzone-accept`;
    }
    if (isDragReject) {
      return `${baseClass} dropzone-reject`;
    }
    if (isDragActive) {
      return `${baseClass} dropzone-active`;
    }
    return file
      ? `${baseClass} dropzone-file-present`
      : `${baseClass} dropzone-default`;
  }, [isDragActive, isDragAccept, isDragReject, file]);

  const isUploadDisabled =
    bulkUploadMutation.isPending ||
    bulkUploadTariffMutation.isPending ||
    isUploading.current ||
    !!activeJobId;

  // ✅ Duplicate Handling Logic (Supports Tariffs)
  const handleDuplicateDecision = async (
    action: 'remove' | 'review',
    selectedDuplicates?: DuplicateEntity[],
  ): Promise<void> => {
    const pendingMutation = isTariffDB
      ? createBulkTariffsWODupeCheck
      : isProductDB
      ? createBulkProductsWODupeCheck
      : createBulkWODupeCheck;

    if (action === 'remove') {
      if (!database?.id || !uploadStartTimeRef.current) {
        enqueueSnackbar(
          'Unable to discard duplicates. Please restart the upload.',
          { variant: 'error' },
        );
        return;
      }

      const payload = isTariffDB
        ? {
            tariffs: [],
            databaseId: database.id,
            createdBy: cur_user.basics?.id || '',
            teamId: cur_user.selected_team?.id || '',
            uploadStartTime: uploadStartTimeRef.current,
          }
        : isProductDB
        ? {
            products: [],
            databaseId: database.id,
            createdBy: cur_user.basics?.id || '',
            teamId: cur_user.selected_team?.id || '',
            uploadStartTime: uploadStartTimeRef.current,
          }
        : {
            entities: [],
            databaseId: database.id,
            createdBy: cur_user.basics?.id || '',
            teamId: cur_user.selected_team?.id || '',
            uploadStartTime: uploadStartTimeRef.current,
          };

      try {
        await new Promise((resolve, reject) => {
          (pendingMutation as any).mutate(payload, {
            onSuccess: () => {
              enqueueSnackbar(
                `${duplicatesFound.length} duplicate${
                  duplicatesFound.length > 1 ? 's' : ''
                } discarded.`,
                { variant: 'success' },
              );
              handleModalClose();
              handleRefresh?.();
              onRequestScreeningCheck?.();
              resolve(null);
            },
            onError: (error: any) => {
              console.error('Error discarding duplicates:', error);
              enqueueSnackbar(
                'Failed to discard duplicates. Please try again.',
                {
                  variant: 'error',
                },
              );
              reject(error);
            },
          });
        });
      } catch (error) {
        console.error('Duplicate removal failed', error);
      }
      return;
    }
    if (pendingMutation.isPending) {
      enqueueSnackbar('Items are being added. Please wait.', {
        variant: 'warning',
      });
      return;
    }

    try {
      const sourceDuplicates = selectedDuplicates || duplicatesFound;
      const itemsToAdd = sourceDuplicates.reduce<Record<string, any>[]>(
        (acc, duplicate) => {
          const payloadEntity = duplicate.entity || (duplicate as any)?.tariff;
          if (!payloadEntity) {
            return acc;
          }

          if (!payloadEntity.database_id && database?.id) {
            acc.push({ ...payloadEntity, database_id: database.id });
          } else {
            acc.push(payloadEntity);
          }
          return acc;
        },
        [],
      );

      if (itemsToAdd.length === 0) {
        enqueueSnackbar('No valid items to add.', { variant: 'error' });
        return;
      }

      const payload = isTariffDB
        ? {
            tariffs: itemsToAdd,
            databaseId: database.id,
            createdBy: cur_user.basics?.id || '',
            teamId: cur_user.selected_team?.id || '',
            uploadStartTime: uploadStartTimeRef.current || undefined,
          }
        : isProductDB
        ? {
            products: itemsToAdd,
            databaseId: database.id,
            createdBy: cur_user.basics?.id || '',
            teamId: cur_user.selected_team?.id || '',
            uploadStartTime: uploadStartTimeRef.current || undefined,
          }
        : {
            entities: itemsToAdd,
            databaseId: database.id,
            createdBy: cur_user.basics?.id || '',
            teamId: cur_user.selected_team?.id || '',
            uploadStartTime: uploadStartTimeRef.current || undefined,
          };

      await new Promise((resolve, reject) => {
        (pendingMutation as any).mutate(payload, {
          onSuccess: () => {
            enqueueSnackbar(
              `${itemsToAdd.length} item${
                itemsToAdd.length > 1 ? 's' : ''
              } added to database successfully.`,
              { variant: 'success' },
            );
            handleModalClose();
            if (handleRefresh) {
              handleRefresh();
            }
            onRequestScreeningCheck?.();
            resolve(null);
          },
          onError: (error: any) => {
            console.error('Error adding items to database:', error);
            enqueueSnackbar('Failed to add items to database', {
              variant: 'error',
            });
            reject(error);
          },
        });
      });
    } catch (error) {
      console.error('Error processing duplicate decision:', error);
    }
  };

  const uploadContent = (
    <div
      className='audit-add-modal-cont-body'
      style={{ paddingTop: '12px', paddingBottom: '12px' }}
    >
      <div className='audit-add-modal-cont-body-frame'>
        <div className='audit-add-modal-cont-body-frame-form'>
          <div className='audit-add-modal-head-cont-supporting'>
            <h1>
              Upload multiple{' '}
              {isTariffDB ? 'tariffs' : isProductDB ? 'products' : 'entities'}{' '}
              using a CSV file
            </h1>
          </div>
          <div className='modal-grid'>
            <div className='modal-section'>
              <h3 className='modal-section-title'>1. Download Template</h3>
              <button
                onClick={handleDownloadTemplate}
                className='button button-primary button-full-width'
              >
                <Download style={{ marginRight: '8px' }} /> Download Template
              </button>
              <p className='modal-section-subtitle'>
                Download a .csv template file with example headers.
              </p>
            </div>
            <div className='modal-section'>
              <h3 className='modal-section-title'>2. Upload File</h3>
              <div {...getRootProps()} className={dropzoneClassName}>
                <input {...getInputProps()} />
                {file ? (
                  <div className='dropzone-content-item'>
                    <svg
                      className='dropzone-icon-success'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      strokeWidth={1.5}
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
                      />
                    </svg>
                    <p className='dropzone-filename'>{file.name}</p>
                    <p className='dropzone-filesize'>
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFile();
                      }}
                      className='dropzone-remove-button'
                    >
                      Remove File
                    </button>
                  </div>
                ) : isUploadDisabled ? (
                  <div className='dropzone-content-item'>
                    Processing file...
                  </div>
                ) : (
                  <div className='dropzone-content-item'>
                    <p className='dropzone-text'>
                      <span className='dropzone-text-highlight'>
                        Click to upload
                      </span>{' '}
                      or drag and drop
                    </p>
                    <p className='dropzone-subtext'>CSV or XLSX (max 10MB)</p>
                    {isDragReject && (
                      <p className='dropzone-error-text'>
                        Invalid file type or size.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className='audit-modal-cont-footer' style={{ width: '100%' }}>
          <div className='audit-modal-cont-footer-content'>
            <div
              className='audit-modal-cont-footer-content-btn-cancel'
              onClick={handleModalClose}
            >
              <div className='audit-modal-cont-footer-content-btn-text'>
                Cancel
              </div>
            </div>
            <div className='audit-modal-cont-footer-content-btn-upload'>
              <div
                className={`audit-modal-cont-footer-content-btn-text ${
                  isUploadDisabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={!isUploadDisabled ? handleBulkUpload : undefined}
                style={{ color: '#FFF' }}
              >
                {isUploadDisabled ? 'Uploading...' : 'Upload'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const duplicateContent = (
    <DuplicateReviewContent
      duplicates={duplicatesFound}
      onDiscard={() => handleDuplicateDecision('remove')}
      onReview={(selectedDuplicates) =>
        handleDuplicateDecision('review', selectedDuplicates)
      }
      isProcessing={
        isTariffDB
          ? createBulkTariffsWODupeCheck.isPending
          : isProductDB
          ? createBulkProductsWODupeCheck.isPending
          : createBulkWODupeCheck.isPending
      }
    />
  );

  const relationshipContent = (
    <div
      className='audit-add-modal-cont-body'
      style={{ paddingTop: '12px', paddingBottom: '12px' }}
    >
      <div className='audit-add-modal-cont-body-frame'>
        <div className='audit-add-modal-cont-body-frame-form'>
          <EntityRelationshipImportStep
            onRelationshipsParsed={setParsedRelationships}
            onDoLater={() => {
              handleRefresh?.();
              handleModalClose();
              onRequestScreeningCheck?.();
            }}
            isUploading={isUploadingRelationships}
          />
          {parsedRelationships.length > 0 && (
            <button
              onClick={handleRelationshipsUpload}
              className='button button-primary button-full-width'
              type='button'
              style={{ marginTop: '12px' }}
              disabled={isUploadingRelationships}
            >
              {isUploadingRelationships
                ? 'Uploading Relationships...'
                : `Upload ${parsedRelationships.length} Relationship${
                    parsedRelationships.length !== 1 ? 's' : ''
                  }`}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const dialogTitle =
    modalStep === 'duplicates' && hasDuplicates
      ? 'Review Potential Duplicates'
      : modalStep === 'relationships'
      ? 'Define Relationships'
      : title;

  return (
    <>
      <Dialog
        className='dialog_box'
        open={open}
        onClose={handleModalClose}
        maxWidth='md'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            overflowY: 'visible',
            maxWidth: '800px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle
          id='bulk-upload-modal-title'
          className='modal-header-content modal-title'
        >
          {dialogTitle}
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={handleModalClose}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
                stroke='var(--text-primary)'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          {modalStep === 'duplicates'
            ? duplicateContent
            : modalStep === 'relationships'
            ? relationshipContent
            : uploadContent}
        </DialogContent>
      </Dialog>
      <InsufficientCredits
        teamId={cur_user?.selected_team?.id || ''}
        isOpen={showInsufficientCredits}
        onClose={() => setShowInsufficientCredits(false)}
        type='entity'
        requiredCredits={requiredDatabaseCredits}
      />
    </>
  );
};
